import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchApi } from "@/lib/api";
import { CLERK_AVAILABLE, getDemoAuthHeaders, getClerkAuthHeaders } from "@/lib/auth";
import { useAuthConditional } from "@/lib/use-auth-conditional";

export interface BudgetLine {
  id: string;
  projectId: string;
  costCodeId: string;
  description: string;
  quantity: number;
  unit: string;
  unitCost: number;
  total: number;
  costCode: {
    id: string;
    code: string;
    name: string;
    division: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CreateBudgetLineData {
  costCodeId: string;
  description: string;
  quantity: number;
  unit: string;
  unitCost: number;
}

export interface UpdateBudgetLineData {
  costCodeId?: string;
  description?: string;
  quantity?: number;
  unit?: string;
  unitCost?: number;
}

export interface BulkImportBudgetLineData {
  costCodeId: string;
  description: string;
  quantity: number;
  unit: string;
  unitCost: number;
}

export interface BulkImportBudgetData {
  lines: BulkImportBudgetLineData[];
}

interface BudgetLinesResponse {
  budgetLines: BudgetLine[];
  totals: {
    totalBudget: number;
    totalLines: number;
    divisionTotals: Record<string, number>;
  };
}

interface BudgetLineResponse {
  budgetLine: BudgetLine;
}

// Get all budget lines for a project with totals
export function useBudget(projectId: string) {
  const auth = useAuthConditional();

  return useQuery({
    queryKey: ["projects", projectId, "budget"],
    queryFn: async () => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<BudgetLinesResponse>(`/api/projects/${projectId}/budget`, {
        headers,
      });
    },
    enabled: auth.isSignedIn && !!projectId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Create a single budget line
export function useCreateBudgetLine(projectId: string) {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateBudgetLineData) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<BudgetLineResponse>(`/api/projects/${projectId}/budget`, {
        method: "POST",
        headers,
        body: JSON.stringify(data),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "budget"] });
    },
  });
}

// Update a budget line
export function useUpdateBudgetLine(projectId: string) {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateBudgetLineData }) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<BudgetLineResponse>(`/api/projects/${projectId}/budget/${id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(data),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "budget"] });
    },
  });
}

// Delete a budget line
export function useDeleteBudgetLine(projectId: string) {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<{ success: boolean }>(`/api/projects/${projectId}/budget/${id}`, {
        method: "DELETE",
        headers,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "budget"] });
    },
  });
}

// Bulk import budget lines
export function useBulkImportBudget(projectId: string) {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: BulkImportBudgetData) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<{ created: number }>(`/api/projects/${projectId}/budget/import`, {
        method: "POST",
        headers,
        body: JSON.stringify(data),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "budget"] });
    },
  });
}
